import { isEmpty } from 'lodash';
import CollectionApi from 'client/api/v2/CollectionApi';
import axios from 'axios';

class UserApi extends CollectionApi {
  /**
   * @description Set up the api for getting admin user information.
   */
  constructor() {
    super('/api/users', {
      skip: 0,
      limit: 50,
      sort: 'email', // Prepend with a '-' to reverse default sort direction
      search: {
        fields: [
          'email',
        ],
        searchTerm: '',
      },
    });
    this.baseUrl = '/api/users';
  }

  /**
   * @description Generates a new API key for this admin user
   */
  generateApiKey() {
    return super.initialize()
      .then(() => axios.put(`${this.baseUrl}/generateApiKey`));
  }

  /**
   * @description Gets information specific to this admin user, e.g.
   *
   *   _id: 'USER_ID'
   *   apiKey: 'API_KEY'
   *   connectkey: 'CONNECT_KEY'
   *   disableIntroduction: false // Unused?
   *   enableMultiFactor: true // Unused?
   *   email: 'admin@jumpcloud.com'
   *   organization: 'ORG_ID' // If the user is a member of a provider
   *   provider: 'PROVIDER_ID' // If the user is a member of a provider
   *   role: 'ROLE_ID'
   *   roleName: 'ROLE_NAME' // 'Administrator With Billing' etc.
   *   usersTimeZone: 'EDT' // Unused?
   */
  getSelf() {
    return super.initialize()
      .then(() => axios.get(`${this.baseUrl}/getSelf`))
      .then(resp => Promise.resolve((resp && resp.data) || ''));
  }

  putSelf(payload) {
    if (isEmpty(payload)) return Promise.reject(new Error('Payload is empty'));
    return super.initialize()
      .then(() => axios.put(`${this.baseUrl}/putSelf`, payload))
      .then(resp => Promise.resolve((resp && resp.data) || ''));
  }

  getUserPreferences() {
    return super.initialize()
      .then(() => axios.get(`${this.baseUrl}/getSelf?fields=preferences`))
      .then(resp => Promise.resolve((resp && resp.data) || ''));
  }

  /**
   * @description Resets an admin user's password
   * @param {String} payload.currentPassword - The admin user's current password
   * @param {String} payload.newPassword - The admin user's new password
   * @param {String} payload.newPassword2 - The admin user's new password
   */
  resetPassword(payload) {
    return super.initialize()
      .then(() => axios.put(`${this.baseUrl}/resetPassword`, payload));
  }

  /**
   * @description Resends a password reset email to the specified id.
   */
  resendEmail(id) {
    return super.initialize()
      .then(() => axios.get(`${this.baseUrl}/reactivate/${id}`))
      .then(resp => (resp && resp.data) || '');
  }

  /**
   * @description Bulk resend password reset email.
   * @param {Array} ids - ids of admins to send email
   */
  resendEmails(ids) {
    return super.initialize()
      .then(() => axios.post(`${this.baseUrl}/reactivate`, {
        isSelectAll: false,
        models: ids.map(id => ({ _id: id })),
      }))
      .then(resp => (resp && resp.data) || '');
  }

  /**
   * @description Resets the TOTP MFA of a specified id.
   * @param {String} id
   */
  resetTOTP(id) {
    return super.initialize()
      .then(() => axios.post(`${this.baseUrl}/resettotp/${id}`));
  }
}

export default new UserApi();
